<template>
  <div class="rich-text-container" v-if="nodes?.length">
    <Scaffold v-for="node in nodes" :key="node.key">
      <component
        v-if="node.nodeType === 'blok'"
        :is="node.blokType"
        :blok="node.content"
      />
      <component v-else :is="node.nodeType" v-html="node.content" />
    </Scaffold>
  </div>
</template>

<script lang="ts" setup>
import { Scaffold } from "cja-phoenix";
import { RichtextStoryblok } from "~/types/bloks";

const { text } = defineProps<{ text: RichtextStoryblok | undefined }>();

const nodes = computed(() => {
  const nodes: any[] = [];

  if (text?.content) {
    Object.entries(text.content).forEach(([key, node]) => {
      if (node.type === "blok" && node.attrs?.body?.length > 0) {
        node.attrs?.body.forEach((blok) => {
          nodes.push({
            key,
            nodeType: "blok",
            blokType: blok.component,
            content: blok,
          });
        });
      } else if (node.content) {
        const nodeType = (() => {
          switch (node.type) {
            case "heading":
              return `h${node.attrs.level}`;

            case "paragraph":
              return "p";

            case "bullet_list":
              return "ul";

            case "ordered_list":
              return "ol";

            case "blockquote":
              return "blockquote";

            default:
              console.info(`Unsupported node: ${node.type}`);
              return "div";
          }
        })();

        nodes.push({
          key,
          nodeType: nodeType,
          content: renderRichText({
            type: "doc",
            content: [node],
          })
            ?.replace(`<${nodeType}>`, "")
            .replace(`</${nodeType}>`, ""),
        });
      }
    });
  } else {
    nodes.push({
      nodeType: "p",
      content: text,
    });
  }

  return nodes;
});
</script>

<style lang="scss" scoped>
.rich-text-container {
  :deep(a:not(.cja-btn)) {
    color: $main-blue;
    transition: all 0.2s ease-in-out;

    &:hover {
      color: $dark-blue;
    }
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin: 20px 0;
  }

  :deep(p),
  :deep(ul) {
    margin: 16px 0;
  }

  :deep(ul) {
    list-style: disc;
    padding-left: 16px;
  }

  * {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
